import { FC, useEffect, useState } from "react"
import { Actions, JsonSchema } from "@jsonforms/core"
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react"
import {
  Box,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material"

import { useAppSelector } from "../../../core/app/hooks"
import { RootState } from "../../../core/app/store"
import { UText } from "../../../ui-component"
import InputSummary from "../NewForm/InputSummary"
import { checkIfObjectHasKeys } from "@/core/utils/formatters"
import { CASE_STATUS } from "@/modules/Patient/config/status.config"

export const UMultipleInput: FC<{
  path: string
  errors: string
  label: string
  schema: JsonSchema
  data: object
  handleChange: (path: string, value: any) => void
}> = ({ path, label, schema, data, handleChange }) => {
  const [value, setValue] = useState<object>({})
  const { core, dispatch } = useJsonForms()
  const [errors, setErrors] = useState("")
  const [isError, setIsError] = useState<boolean>(true)
  const [updatedSchema, setUpdatedSchema] = useState<object>({})

  const { caseDetail, caseList } = useAppSelector((state: RootState) => state.caseService)

  const { stageObjBySlice } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const { upList, downList } = stageObjBySlice

  useEffect(() => {
    setValue(data)
  }, [data])

  useEffect(() => {
    // update the default stage number
    if (caseDetail?.udesign_json) {
      const udesign_json = JSON.parse(caseDetail.udesign_json)
      const upperStage = udesign_json.UpperMaxStageNum
      const lowerStage = udesign_json.LowerMaxStageNum
      const tempenum = []
      if (udesign_json.UpperType === "A" || udesign_json.UpperType === "R") {
        tempenum.push({ name: "Upper", enum: [] })
      }
      if (udesign_json.LowerType === "A" || udesign_json.LowerType === "R") {
        tempenum.push({ name: "Lower", enum: [] })
      }

      tempenum.map((item) => {
        if (caseDetail.caseDisposition === CASE_STATUS.ATTENTION_NEEDED) {
          const maxStage = item.name === "Upper" ? upperStage : lowerStage
          if (maxStage) {
            for (let i = 1; i <= maxStage; i++) {
              item.enum.push(i)
            }
          }
        } else {
          const list = item.name === "Upper" ? upList : downList
          if (list && list.length > 0) {
            for (let i = 0; i < list.length; i++) {
              item.enum.push(list[i].showStage)
            }
          } else {
            item.enum.push(0)
          }
        }
      })
      setUpdatedSchema({
        ...schema,
        inputGroup: tempenum,
      })
    }
  
  }, [schema, upList, downList, caseDetail])
  

  useEffect(() => {
    const hasValues = () => {
      for (const key in data) {
        // eslint-disable-next-line no-prototype-builtins
        if (data.hasOwnProperty(key) && data[key] !== undefined && data[key] !== null) {
          return false
        }
      }
      return true
    }
    const hasNoData = data && hasValues()
    setIsError(hasNoData)
    if (core?.schema.required?.indexOf(path) === -1) return
    if (hasNoData) {
      updateErrors("This is a required field")
    } else {
      updateErrors("")
    }
  }, [data])

  const updateErrors = (message: string) => {
    setErrors(message)
    dispatch &&
      dispatch(
        Actions.updateErrors([
          {
            instancePath: "/" + path,
            message,
            schemaPath: "",
            keyword: "",
            params: {},
          },
        ]),
      )
  }

  const generateSummary = (schema, data) => {
    return schema.inputGroup
      .map((input) => {
        const value = data[input.name]
        return value !== undefined && value !== null ? `${input.name} ${value}` : null
      })
      .filter(Boolean)
      .join("\n")
  }

  return (
    <>
      {schema?.summary ? (
        <InputSummary label={label} inputVal={generateSummary(schema, data)} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "row", my: 1 }}>
          <UText
            variant={"body2"}
            sxProp={{
              maxWidth: 210,
              color:
                errors && core?.validationMode === "ValidateAndShow"
                  ? "error.main"
                  : "text.secondary",
            }}
          >
            {label}
          </UText>
          <Box>
            {checkIfObjectHasKeys(updatedSchema) &&
            updatedSchema?.inputGroup.map((inputField) => (
                <FormControl
                  key={inputField.name}
                  error={
                    !!(errors && core?.validationMode === "ValidateAndShow")
                  }
                  sx={{ mt: 1, display: "flex", flexDirection: "row", pl: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1" sx={{ minWidth: "96px" }}>
                      {inputField.name}
                    </Typography>
                    <Select
                      value={value[inputField.name] || "0"}
                      sx={{
                        width: "80px",
                      }}
                      onChange={(event: any) => {
                        const inputValue = event.target.value
                        handleChange(path, {
                          ...value,
                          [inputField.name]: inputValue,
                        })
                        // if (!isNaN(inputValue)) {
                        //   handleChange(path, {
                        //     ...value,
                        //     [inputField.name]: inputValue,
                        //   })
                        // }
                      }}
                    >
                      {inputField.name === "Upper"
                        ? inputField.enum?.map((e, i) =>
                            !isNaN(Number(e)) ? (
                              <MenuItem key={`${e}+${i}`} value={e}>
                                {e}
                              </MenuItem>
                            ) : null,
                          )
                        : inputField.enum?.map((e, i) =>
                            !isNaN(Number(e)) ? (
                              <MenuItem key={`${e}+${i}`} value={e}>
                                {e}
                              </MenuItem>
                            ) : null,
                          )}
                    </Select>
                  </Box>
                </FormControl>
              ))}
            {isError && core?.validationMode === "ValidateAndShow" && (
              <UText
                variant={"caption"}
                sxProp={{ marginLeft: 1, color: "error.main" }}
              >
                {errors}
              </UText>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

export default withJsonFormsControlProps(UMultipleInput)
